'use client';

import { useState, useMemo, useCallback } from 'react';
import {
  MapPin,
  Buildings,
  House,
  Clock,
  CurrencyRub,
  MagnifyingGlass,
  X,
  CaretUp,
  CaretDown,
  FunnelSimple,
  MapTrifold,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/* ─── Types ─── */

interface ZoneRow {
  name: string;
  price: string;
  priceMin: number;
  time?: string;
  timeMin?: number;
  category: 'district' | 'micro' | 'complex' | 'bataysk' | 'aksay';
}

type SortField = 'name' | 'price' | 'time';
type SortDir = 'asc' | 'desc';

/* ─── Стоимость доставки по зонам ─── */
// Бесплатную доставку убрали — показываем реальную стоимость каждой зоны.
// Порог = 0: ни одна зона не превращается в "Бесплатно" (applyFreeDelivery
// возвращает зоны как есть). Бесплатная доставка теперь считается по СУММЕ
// заказа в корзине (для отдельных зон), а не по стоимости зоны.
const FREE_DELIVERY_THRESHOLD = 0;

/**
 * Преобразует исходную зону (с реальными ценами) в зону с применённым
 * правилом бесплатной доставки.
 *
 * Логика:
 *   • Если priceMin >= порог → оставляем как было (платная зона)
 *   • Если price = "165 ₽" и 165 < порог → "Бесплатно"
 *   • Если price = "125–195 ₽" и оба значения < порог → "Бесплатно"
 *   • Если price = "165–295 ₽" (нижняя < порог, верхняя >= порог) →
 *     "от 0 до 295 ₽" (часть адресов внутри зоны платная — честно
 *     показываем диапазон, чтобы юзер не удивился при оформлении).
 *
 * priceMin = 0 для всех "бесплатных" — чтобы при сортировке шли первыми.
 */
function applyFreeDelivery(zone: ZoneRow): ZoneRow {
  if (zone.priceMin >= FREE_DELIVERY_THRESHOLD) return zone;

  const rangeMatch = zone.price.match(/^(\d+)–(\d+)\s*₽$/);
  if (rangeMatch) {
    const high = parseInt(rangeMatch[2], 10);
    if (high >= FREE_DELIVERY_THRESHOLD) {
      return { ...zone, price: `от 0 до ${high} ₽`, priceMin: 0 };
    }
  }
  return { ...zone, price: 'Бесплатно', priceMin: 0 };
}

/* ─── Data ─── */

const RAW_ZONES: ZoneRow[] = [
  // Административные районы
  { name: 'Кировский район', price: '165 ₽', priceMin: 165, category: 'district' },
  { name: 'Ленинский район', price: '125–195 ₽', priceMin: 125, category: 'district' },
  { name: 'Ворошиловский район', price: '165–295 ₽', priceMin: 165, category: 'district' },
  { name: 'Советский район', price: '165–295 ₽', priceMin: 165, category: 'district' },
  { name: 'Железнодорожный район', price: '165–195 ₽', priceMin: 165, category: 'district' },
  { name: 'Пролетарский район', price: '165–325 ₽', priceMin: 165, category: 'district' },
  { name: 'Первомайский район', price: '245–295 ₽', priceMin: 245, category: 'district' },
  { name: 'Октябрьский район', price: '195–295 ₽', priceMin: 195, category: 'district' },
  { name: 'Аксайский район', price: '325 ₽', priceMin: 325, category: 'district' },
  { name: 'Мясниковский район', price: '295 ₽', priceMin: 295, category: 'district' },

  // Микрорайоны
  { name: 'Левенцовский', price: '195 ₽', priceMin: 195, category: 'micro' },
  { name: 'Западный', price: '165–295 ₽', priceMin: 165, time: '80–120 мин', timeMin: 80, category: 'micro' },
  { name: 'Ленинаван', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Змиевка', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'micro' },
  { name: 'Олимпиадовка', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'micro' },
  { name: 'Ленгородок', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'micro' },
  { name: 'Военвед', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'micro' },
  { name: 'Стройгородок', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'micro' },
  { name: 'Каменка', price: '125 ₽', priceMin: 125, time: '60 мин', timeMin: 60, category: 'micro' },
  { name: 'Суворовский', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Темерницкий', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Верхнетемерницкий', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Северный', price: '195–295 ₽', priceMin: 195, time: '90–110 мин', timeMin: 90, category: 'micro' },
  { name: 'Мирный', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'micro' },
  { name: 'Чкаловский', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'micro' },
  { name: 'Камышеваха', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Водопадный', price: '325 ₽', priceMin: 325, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Янтарный', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: '2-й п. Орджоникидзе', price: '245 ₽', priceMin: 245, time: '90 мин', timeMin: 90, category: 'micro' },
  { name: 'Сельмаш', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'micro' },
  { name: 'Новое поселение', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'micro' },
  { name: 'Рабочий городок', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'micro' },
  { name: 'Берберовка', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'micro' },
  { name: 'Нахичевань', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'micro' },
  { name: 'Александровка', price: '195 ₽', priceMin: 195, time: '110 мин', timeMin: 110, category: 'micro' },
  { name: 'Кирпичный', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'micro' },
  { name: 'Ленинакан', price: '195 ₽', priceMin: 195, time: '110 мин', timeMin: 110, category: 'micro' },

  // ЖК Ростова
  { name: 'Флора', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Звезда столицы', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Рижский уют', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Скай парк', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Панорама на Театральном', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Западные Аллеи', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Притяжение', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Октябрь Парк', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: '5 Элемент-монолит', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Суворовский', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: '1799', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Сельмаш', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Вересаево', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Красный Аксай', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Манхэттен', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Сокол Градъ', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Сердце Ростова', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Сердце Ростова 2', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Свобода', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Грин Парк', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Город у реки', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Державинский', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Голубые ели', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Измаильский парк', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Тихий Дон', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Спутник', price: '295 ₽', priceMin: 295, time: '120 мин', timeMin: 120, category: 'complex' },
  { name: 'Самоцветы', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'complex' },
  { name: 'Космос', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Новый Сельмаш', price: '245 ₽', priceMin: 245, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Дмитровград', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Сиреневый квартал', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Ривьера', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Березовая роща', price: '245 ₽', priceMin: 245, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Гагарин', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Семья', price: '245 ₽', priceMin: 245, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Чехов', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Рубин', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Звездный', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Екатерининский', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Мечников', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Гранд Панорама', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Аврора', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Смартполет', price: '295 ₽', priceMin: 295, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Династия', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: '61 Квартал', price: '325 ₽', priceMin: 325, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Левобережье', price: '295 ₽', priceMin: 295, time: '110 мин', timeMin: 110, category: 'complex' },
  { name: 'Парк Островского 2', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Гринсайд', price: '125 ₽', priceMin: 125, time: '60 мин', timeMin: 60, category: 'complex' },
  { name: 'Буденновский', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Новый Горизонт', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Лайм сити', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Соловьиная роща', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Поляна', price: '295 ₽', priceMin: 295, time: '120 мин', timeMin: 120, category: 'complex' },
  { name: 'Максим Горький', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Квадро', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Первый', price: '125 ₽', priceMin: 125, time: '60 мин', timeMin: 60, category: 'complex' },
  { name: 'Соборный', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Горизонт', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Lime City', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Вместе', price: '195 ₽', priceMin: 195, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Адмирал', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Волково', price: '195 ₽', priceMin: 195, time: '90 мин', timeMin: 90, category: 'complex' },
  { name: 'Сармат Тауэр', price: '245 ₽', priceMin: 245, time: '100 мин', timeMin: 100, category: 'complex' },
  { name: 'Доломановский', price: '165 ₽', priceMin: 165, time: '80 мин', timeMin: 80, category: 'complex' },
  { name: 'Площадь Ленина', price: '125 ₽', priceMin: 125, time: '50 мин', timeMin: 50, category: 'complex' },
  { name: 'Манхэттен О2', price: '195 ₽', priceMin: 195, time: '110 мин', timeMin: 110, category: 'complex' },

  // Батайск
  { name: 'Темп', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'bataysk' },
  { name: 'Солнечный Город', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Пульс', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Комсомольский', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: '65', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Парк Культуры', price: '325 ₽', priceMin: 325, time: '140 мин', timeMin: 140, category: 'bataysk' },
  { name: 'Новая Пальмира', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Встреча', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Булгаков', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Монте-Карло', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Южный Берег', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'ТеслаДом', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Северная Звезда', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Пальмира 2.0', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Парк Авиаторов', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Прибрежный', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Парк', price: '325 ₽', priceMin: 325, time: '140 мин', timeMin: 140, category: 'bataysk' },
  { name: 'На ул. Воровского, 63', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },
  { name: 'Северное Сияние', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'bataysk' },

  // Аксай
  { name: 'Простор', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Вишневый сад', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Новый Аксай', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Самоцветы', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Дом на Аллее', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Аксинья', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Парк Флора', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Березка', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Твой Дом', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Березовый квартал', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'По ул. Менделеева, 53а', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'По ул. Фрунзе, 1/10', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Суворов', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'aksay' },
  { name: 'По ул. Заводской, 20', price: '325 ₽', priceMin: 325, time: '120 мин', timeMin: 120, category: 'aksay' },
  { name: 'Советская 11', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'На ул. Д. Бедного, 5', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
  { name: 'Мегаполис', price: '325 ₽', priceMin: 325, time: '110 мин', timeMin: 110, category: 'aksay' },
];

// applyFreeDelivery сейчас no-op (порог 0) — зоны идут с реальными ценами.
// Чтобы вернуть бесплатные зоны — поднять FREE_DELIVERY_THRESHOLD выше.
const ALL_ZONES: ZoneRow[] = RAW_ZONES.map(applyFreeDelivery);

/* ─── Category config ─── */

const CATEGORIES = [
  { id: 'all' as const, label: 'Все', icon: MapTrifold, count: ALL_ZONES.length },
  { id: 'district' as const, label: 'Районы', icon: MapPin, count: ALL_ZONES.filter((z) => z.category === 'district').length },
  { id: 'micro' as const, label: 'Микрорайоны', icon: House, count: ALL_ZONES.filter((z) => z.category === 'micro').length },
  { id: 'complex' as const, label: 'ЖК Ростова', icon: Buildings, count: ALL_ZONES.filter((z) => z.category === 'complex').length },
  { id: 'bataysk' as const, label: 'Батайск', icon: Buildings, count: ALL_ZONES.filter((z) => z.category === 'bataysk').length },
  { id: 'aksay' as const, label: 'Аксай', icon: Buildings, count: ALL_ZONES.filter((z) => z.category === 'aksay').length },
] as const;

type CategoryId = (typeof CATEGORIES)[number]['id'];

/* ─── Price tag styles ─── */

// Платная зона — розовый бейдж (в тон платным зонам на карте)
const PRICE_BADGE = 'bg-[#EC4899]/10 text-[#EC4899] border-[#EC4899]/20';
const PRICE_DOT = 'bg-[#EC4899]';

// Бесплатная зона — фиолетовый бейдж в фирменных цветах (в тон бесплатным зонам на карте).
// Используется в строке если price.includes('Бесплатно').
const FREE_BADGE = 'bg-[var(--primary-subtle)] text-[var(--primary)] border-[var(--primary)]/15';
const FREE_DOT = 'bg-[var(--primary)]';

/* ─── Sort header button ─── */

function SortButton({
  label,
  field,
  currentField,
  currentDir,
  onSort,
  className,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (f: SortField) => void;
  className?: string;
}) {
  const isActive = currentField === field;
  return (
    <button
      onClick={() => onSort(field)}
      className={cn(
        'flex items-center gap-1 text-xs font-bold uppercase tracking-wider transition-colors group',
        isActive ? 'text-white' : 'text-white/70 hover:text-white',
        className,
      )}
    >
      {label}
      <span className="flex flex-col -space-y-1">
        <CaretUp
          weight="bold"
          className={cn(
            'w-3 h-3 transition-opacity',
            isActive && currentDir === 'asc' ? 'opacity-100' : 'opacity-30 group-hover:opacity-60',
          )}
        />
        <CaretDown
          weight="bold"
          className={cn(
            'w-3 h-3 transition-opacity',
            isActive && currentDir === 'desc' ? 'opacity-100' : 'opacity-30 group-hover:opacity-60',
          )}
        />
      </span>
    </button>
  );
}

/* ─── Highlight matched text ─── */

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-[var(--primary)]/20 text-[var(--primary)] rounded-sm px-0.5 font-semibold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

/* ─── Main component ─── */

export function DeliveryZones() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryId>('all');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField],
  );

  const filtered = useMemo(() => {
    let rows = ALL_ZONES;

    // Category filter
    if (category !== 'all') {
      rows = rows.filter((r) => r.category === category);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }

    // Sort
    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name, 'ru');
          break;
        case 'price':
          cmp = a.priceMin - b.priceMin;
          break;
        case 'time':
          cmp = (a.timeMin ?? 999) - (b.timeMin ?? 999);
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return rows;
  }, [search, category, sortField, sortDir]);

  /* JSON-LD structured data */
  const districtRows = ALL_ZONES.filter((z) => z.category === 'district');
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    name: 'Азиаточка',
    url: 'https://aziatochka.ru',
    areaServed: districtRows.map((d) => ({
      '@type': 'Place',
      name: `${d.name}, Ростов-на-Дону`,
    })),
  };

  return (
    <section id="zones" className="py-16 sm:py-24 relative overflow-hidden" aria-labelledby="delivery-zones-heading">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="absolute inset-0 bg-[var(--bg-primary)]" />
      <div className="blur-blob absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-[var(--primary)]/8 blur-[120px]" />
      <div className="blur-blob absolute bottom-20 -right-40 w-[400px] h-[400px] rounded-full bg-[var(--violet-400)]/6 blur-[100px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14 animate-fade-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] text-sm font-semibold mb-4">
            <MapPin weight="fill" className="w-4 h-4" />
            Зоны доставки
          </span>
          <h2 id="delivery-zones-heading" className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight text-[var(--text-primary)] mb-4">
            Стоимость доставки{' '}
            <span className="text-[var(--primary)]">по районам Ростова‑на‑Дону</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg max-w-2xl mx-auto">
            Найдите свой район, микрорайон или ЖК и узнайте стоимость и время доставки
          </p>
        </div>

        {/* Toolbar: search + categories */}
        <div className="animate-fade-up space-y-4 mb-6" style={{ animationDelay: '80ms' }}>
          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)] pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Найти район, микрорайон или ЖК..."
              className={cn(
                'w-full pl-12 pr-10 py-3.5 rounded-2xl text-sm',
                'bg-[var(--surface-primary)] border border-[var(--border-default)]',
                'text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 focus:border-[var(--primary)]',
                'transition-all duration-200',
              )}
              aria-label="Поиск по зонам доставки"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[var(--bg-secondary)] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
                aria-label="Очистить поиск"
              >
                <X weight="bold" className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Category cards */}
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
            {CATEGORIES.map((cat) => {
              const isActive = cat.id === category;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  aria-pressed={isActive}
                  className={cn(
                    'group relative flex flex-col items-center gap-1.5 sm:gap-2 px-3 py-3 sm:py-4 rounded-2xl text-center transition-all duration-200',
                    isActive
                      ? 'bg-[var(--primary)] text-white shadow-lg shadow-[var(--primary)]/25 scale-[1.02]'
                      : 'bg-[var(--surface-primary)] border border-[var(--border-default)] hover:border-[var(--primary)]/30 hover:shadow-md hover:shadow-[var(--primary)]/5 hover:-translate-y-0.5',
                  )}
                >
                  <div
                    className={cn(
                      'w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-200',
                      isActive
                        ? 'bg-white/20'
                        : 'bg-[var(--primary-subtle)] group-hover:bg-[var(--primary)]/15',
                    )}
                  >
                    <cat.icon
                      weight={isActive ? 'fill' : 'duotone'}
                      className={cn(
                        'w-4.5 h-4.5 sm:w-5 sm:h-5 transition-colors',
                        isActive ? 'text-white' : 'text-[var(--primary)]',
                      )}
                    />
                  </div>
                  <span className={cn(
                    'text-xs sm:text-sm font-semibold leading-tight',
                    isActive ? 'text-white' : 'text-[var(--text-primary)]',
                  )}>
                    {cat.label}
                  </span>
                  <span
                    className={cn(
                      'text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full',
                      isActive
                        ? 'bg-white/20 text-white'
                        : 'bg-[var(--bg-secondary)] text-[var(--text-tertiary)]',
                    )}
                  >
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results count + legend */}
        <div className="flex items-center justify-between mb-3 animate-fade-up" style={{ animationDelay: '120ms' }}>
          <p className="text-sm text-[var(--text-tertiary)]">
            {filtered.length === 0 ? (
              'Ничего не найдено'
            ) : (
              <>
                Найдено: <span className="font-semibold text-[var(--text-secondary)]">{filtered.length}</span>
              </>
            )}
          </p>
          <div className="hidden sm:flex items-center gap-3 text-xs text-[var(--text-tertiary)]">
            <FunnelSimple className="w-3.5 h-3.5" />
            <span>Нажмите на заголовок для сортировки</span>
          </div>
        </div>

        {/* Table */}
        <div className="animate-fade-up rounded-2xl overflow-hidden border border-[var(--border-default)] bg-[var(--surface-primary)] shadow-sm" style={{ animationDelay: '160ms' }}>
          {/* Table header */}
          <div
            className="grid grid-cols-[1fr_auto_auto] gap-2 sm:gap-4 px-4 sm:px-6 py-3.5 items-center"
            style={{ background: 'linear-gradient(135deg, #7B42FF 0%, #5B28D1 100%)' }}
          >
            <SortButton label="Название" field="name" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
            <SortButton label="Стоимость" field="price" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="justify-end" />
            <SortButton label="Время" field="time" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="justify-end min-w-[80px] sm:min-w-[100px]" />
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <MagnifyingGlass className="w-10 h-10 text-[var(--text-tertiary)]" />
              <p className="text-[var(--text-secondary)] font-medium">Ничего не найдено</p>
              <p className="text-sm text-[var(--text-tertiary)]">Попробуйте изменить запрос или выбрать другую категорию</p>
              <button
                onClick={() => { setSearch(''); setCategory('all'); }}
                className="mt-2 px-4 py-2 rounded-full text-sm font-medium bg-[var(--primary-subtle)] text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors"
              >
                Сбросить фильтры
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[var(--border-default)] max-h-[600px] overflow-y-auto overscroll-contain">
              {filtered.map((row, i) => {
                // Для зон где есть хотя бы часть бесплатных адресов —
                // зелёный акцент. Это и "Бесплатно", и "от 0 до N ₽".
                // Различаем по priceMin = 0 после applyFreeDelivery.
                const isFree = row.priceMin === 0;
                const dotClass = isFree ? FREE_DOT : PRICE_DOT;
                const badgeClass = isFree ? FREE_BADGE : PRICE_BADGE;
                return (
                <div
                  key={`${row.category}-${row.name}`}
                  className={cn(
                    'grid grid-cols-[1fr_auto_auto] gap-2 sm:gap-4 px-4 sm:px-6 py-3 sm:py-3.5 items-center transition-all duration-150',
                    i % 2 === 0 ? 'bg-[var(--surface-primary)]' : 'bg-[var(--bg-secondary)]/50',
                    'hover:bg-[var(--primary-subtle)] hover:scale-[1.002]',
                  )}
                >
                  {/* Name */}
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={cn('w-2 h-2 rounded-full shrink-0', dotClass)} />
                    <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                      <Highlight text={row.name} query={search} />
                    </span>
                  </div>

                  {/* Price badge */}
                  <span
                    className={cn(
                      'inline-flex items-center px-2.5 py-1 rounded-lg text-xs sm:text-sm font-bold border whitespace-nowrap',
                      badgeClass,
                    )}
                  >
                    {row.price}
                  </span>

                  {/* Time */}
                  <span className="text-xs sm:text-sm text-[var(--text-secondary)] text-right whitespace-nowrap min-w-[80px] sm:min-w-[100px] flex items-center gap-1 justify-end">
                    {row.time ? (
                      <>
                        <Clock weight="regular" className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" />
                        {row.time}
                      </>
                    ) : (
                      <span className="text-[var(--text-tertiary)]">—</span>
                    )}
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SEO text */}
        <div className="mt-10 rounded-2xl border border-[var(--border-default)] bg-[var(--surface-primary)] p-6 sm:p-8 animate-fade-up" style={{ animationDelay: '320ms' }}>
          <h3 className="text-lg font-bold text-[var(--text-primary)] mb-3">
            Доставка роллов и суши по районам Ростова‑на‑Дону
          </h3>
          <div className="text-sm text-[var(--text-secondary)] leading-relaxed space-y-3">
            <p>
              Азиаточка доставляет роллы, суши и блюда японской кухни по всем районам Ростова‑на‑Дону:
              Кировский, Ленинский, Ворошиловский, Советский, Железнодорожный, Пролетарский,
              Первомайский и Октябрьский районы. Также доставляем в Батайск и Аксай.
            </p>
            <p>
              <strong className="text-[var(--text-primary)]">Стоимость доставки</strong> зависит
              от зоны — от 125 ₽ по центральным районам (Кировский, Ленинский, Ворошиловский,
              Советский, Железнодорожный, Пролетарский) до 245–325 ₽ для удалённых направлений
              (Аксай, Батайск, Мясниковский район, отдельные ЖК). Среднее время доставки —
              от 50 до 140 минут. Мы доставляем в более чем 60 жилых комплексов Ростова‑на‑Дону,
              включая Суворовский, Левенцовский, Западный и другие микрорайоны.
            </p>
            <p>
              Работаем ежедневно с 11:00 до 22:00. Проверьте ваш адрес на карте выше, чтобы узнать
              точную стоимость и время доставки.
            </p>
          </div>
        </div>

        <p className="text-center text-sm text-[var(--text-tertiary)] mt-6">
          * Стоимость и время доставки могут варьироваться в зависимости от загруженности и погодных условий
        </p>
      </div>
    </section>
  );
}
